<script>
import { FormButton } from '@/components/form';
import SelectField from '@/components/form/SelectField.vue';
import TextInput from '@/components/form/TextInput.vue';
import FloorplanFeatures from '@/models/floorplanfeatures';
import FloorplanFeaturesEnum from '@/enums/FloorplanFeaturesEnum';
import { GlobalPolicy } from '@/policies/global';

export default {
    setup() {
        return { GlobalPolicy };
    },
    data() {
        return {
            index: null,
            draft: null,
            featuresEnum: FloorplanFeaturesEnum,
        };
    },
    emits: ['update'],
    computed: {
        availableAmenitiesForCharacteristic() {
            return (characteristicIndex) => {
                const currentValue = this.draft?.characteristics[characteristicIndex]?.amenity ?? null;
                const usedElsewhere = this.item.meta.features
                    .flatMap((f, fi) => {
                        if (fi === this.index) {
                            return this.draft.characteristics
                                .filter((_, ci) => ci !== characteristicIndex)
                                .map(c => c.amenity);
                        }
                        return f.characteristics.map(c => c.amenity);
                    })
                    .filter(a => a !== null);

                return Object.entries(FloorplanFeaturesEnum)
                    .filter(([key]) => key === currentValue || !usedElsewhere.includes(key))
                    .map(([value, label]) => ({ value, label }));
            };
        },
    },
    methods: {
        edit(i) {
            this.index = i;
            this.draft = JSON.parse(JSON.stringify(this.item.meta.features[i]));
        },
        addGroup() {
            this.index = -1;
            this.draft = { group: '', characteristics: [] };
        },
        addCharacteristic() {
            this.draft.characteristics.push({ feature: '', amenity: null });
        },
        removeCharacteristic(i) {
            this.draft.characteristics.splice(i, 1);
        },
        save() {
            const item = this.item;
            const feature = new FloorplanFeatures(this.draft);
            if (this.index === -1) {
                item.meta.features.push(feature);
            } else {
                item.meta.features.splice(this.index, 1, feature);
            }
            this.$emit('update', item);
            this.cancel();
        },
        drop(i) {
            const item = this.item;
            item.meta.features.splice(i, 1);
            this.$emit('update', item);
            this.cancel();
        },
        cancel() {
            this.index = null;
            this.draft = null;
        },
    },
    props: {
        item: {
            type: Object,
            required: true,
        },
    },
    components: { FormButton, SelectField, TextInput },
};
</script>

<template>
    <div class="flex flex-col lg:flex-row justify-between items-center mb-4">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Features</h2>
        <FormButton v-if="GlobalPolicy.edit()" color="blue" type="button" @click="addGroup">
            <span class="fak fa-plus mr-1"></span>Add Group
        </FormButton>
    </div>

    <div v-if="item.meta.features.length === 0 && index === null" class="text-sm text-oakwood-gray-500 py-4">
        There are currently no feature groups.
    </div>

    <div v-for="(feature, i) in item.meta.features" :key="i" class="border border-oakwood-gray-200 rounded-lg mb-4">

        <!-- View mode -->
        <div v-if="index !== i">
            <div class="flex items-center justify-between px-4 py-3 bg-bhav-cool-gray-50 rounded-t-lg">
                <h3 class="font-medium text-bhav-blue-600">{{ feature.group || '(No Group Name)' }}</h3>
                <div v-if="GlobalPolicy.edit()" class="flex gap-2">
                    <FormButton type="button" color="blue" icon="fa-pen" prefix-class="fak" @click="edit(i)">
                        Edit
                    </FormButton>
                    <FormButton type="button" color="red" icon="fa-trash" prefix-class="fak" @click="drop(i)" />
                </div>
            </div>
            <ul class="px-4 py-3 space-y-1">
                <li v-if="feature.characteristics.length === 0" class="text-sm text-oakwood-gray-400 italic">
                    No characteristics.
                </li>
                <li v-for="(c, ci) in feature.characteristics" :key="ci" class="text-sm text-oakwood-gray-700 flex items-start gap-2">
                    <span class="mt-1 text-bhav-blue-400">•</span>
                    <span>
                        {{ c.feature }}
                        <span v-if="c.amenity" class="ml-1 text-xs text-oakwood-gray-400">({{ featuresEnum[c.amenity] ?? c.amenity }})</span>
                    </span>
                </li>
            </ul>
        </div>

        <!-- Edit mode (inline) -->
        <div v-else>
            <div class="flex items-center justify-between px-4 py-3 bg-bhav-blue-50 rounded-t-lg">
                <div class="flex-1 mr-4">
                    <label class="block text-xs font-medium text-oakwood-gray-700 mb-1">Group Name</label>
                    <TextInput v-model="draft.group" class="w-full" placeholder="Group name" />
                </div>
                <div class="flex gap-2 shrink-0">
                    <FormButton type="button" color="blue" @click="save">Save</FormButton>
                    <FormButton type="button" color="gray" @click="cancel">Cancel</FormButton>
                </div>
            </div>

            <div class="px-4 py-3 space-y-3">
                <div
                    v-for="(c, ci) in draft.characteristics"
                    :key="ci"
                    class="grid grid-cols-12 gap-3 items-end border-b border-oakwood-gray-100 pb-3"
                >
                    <div class="col-span-5">
                        <label class="block text-xs font-medium text-oakwood-gray-700 mb-1">Feature</label>
                        <TextInput v-model="draft.characteristics[ci].feature" class="w-full" placeholder="Feature description" />
                    </div>
                    <div class="col-span-6">
                        <label class="block text-xs font-medium text-oakwood-gray-700 mb-1">Amenity (optional)</label>
                        <SelectField
                            v-model="draft.characteristics[ci].amenity"
                            :options="availableAmenitiesForCharacteristic(ci)"
                            :allowNull="true"
                            empty="None"
                            class="w-full"
                        />
                    </div>
                    <div class="col-span-1 flex justify-end pb-1">
                        <FormButton type="button" color="red" icon="fa-trash" prefix-class="fak" @click="removeCharacteristic(ci)" />
                    </div>
                </div>

                <FormButton type="button" color="gray" @click="addCharacteristic">
                    <span class="fak fa-plus mr-1"></span>Add Characteristic
                </FormButton>
            </div>
        </div>

    </div>

    <!-- New group form (not yet in the list) -->
    <div v-if="index === -1" class="border border-bhav-blue-200 rounded-lg mb-4">
        <div class="flex items-center justify-between px-4 py-3 bg-bhav-blue-50 rounded-t-lg">
            <div class="flex-1 mr-4">
                <label class="block text-xs font-medium text-oakwood-gray-700 mb-1">Group Name</label>
                <TextInput v-model="draft.group" class="w-full" placeholder="Group name" />
            </div>
            <div class="flex gap-2 shrink-0">
                <FormButton type="button" color="blue" @click="save">Save</FormButton>
                <FormButton type="button" color="gray" @click="cancel">Cancel</FormButton>
            </div>
        </div>

        <div class="px-4 py-3 space-y-3">
            <div
                v-for="(c, ci) in draft.characteristics"
                :key="ci"
                class="grid grid-cols-12 gap-3 items-end border-b border-oakwood-gray-100 pb-3"
            >
                <div class="col-span-5">
                    <label class="block text-xs font-medium text-oakwood-gray-700 mb-1">Feature</label>
                    <TextInput v-model="draft.characteristics[ci].feature" class="w-full" placeholder="Feature description" />
                </div>
                <div class="col-span-6">
                    <label class="block text-xs font-medium text-oakwood-gray-700 mb-1">Amenity (optional)</label>
                    <SelectField
                        v-model="draft.characteristics[ci].amenity"
                        :options="availableAmenitiesForCharacteristic(ci)"
                        :allowNull="true"
                        empty="None"
                        class="w-full"
                    />
                </div>
                <div class="col-span-1 flex justify-end pb-1">
                    <FormButton type="button" color="red" icon="fa-trash" prefix-class="fak" @click="removeCharacteristic(ci)" />
                </div>
            </div>

            <FormButton type="button" color="gray" @click="addCharacteristic">
                <span class="fak fa-plus mr-1"></span>Add Characteristic
            </FormButton>
        </div>
    </div>
</template>
